import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
	StyledContainer,
	StyledDropContainer,
	StyledUploadIcon,
	StyledDropMessage,
	StyledFileDisplayContainer,
	StyledFileStatusBar,
	StyledFileType,
	StyledFileName,
	StyledFileErrorMessage,
	StyledFileTypeLogo,
	StyledFileSize,
	StyledFileRemove,
	StyledActionButtons,
	StyledErrorMessage,
} from "../../styles/StyledDropzone";
import { Button } from "../Button";
import SaveIcon from "../../assets/img/icons/save.svg";

const Dropzone = ({ single, validTypes, cb }) => {
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [errorMessage, setErrorMessage] = useState("");
	const [validFiles, setValidFiles] = useState([]);
	const [unsupportedFiles, setUnsupportedFiles] = useState([]);

	const fileInputRef = useRef();

	useEffect(() => {
		let filteredArray = selectedFiles.reduce((file, current) => {
			const x = file.find((item) => item.name === current.name);
			if (!x) {
				return file.concat([current]);
			} else {
				return file;
			}
		}, []);
		setValidFiles([...filteredArray]);
	}, [selectedFiles]);

	const dragOver = (e) => {
		e.preventDefault();
	};

	const dragEnter = (e) => {
		e.preventDefault();
	};

	const dragLeave = (e) => {
		e.preventDefault();
	};

	const fileDrop = (e) => {
		e.preventDefault();
		const files = e.dataTransfer.files;
		if (files.length) {
			handleFiles(files);
		}
	};

	const handleFiles = (files) => {
		for (let i = 0; i < files.length; i++) {
			if (validateFile(files[i])) {
				setSelectedFiles((prevArray) => [...prevArray, files[i]]);
			} else {
				files[i]["invalid"] = true;
				setSelectedFiles((prevArray) => [...prevArray, files[i]]);
				setErrorMessage("File type not permitted");
				setUnsupportedFiles((prevArray) => [...prevArray, files[i]]);
			}
		}
	};

	const fileInputClicked = () => {
		fileInputRef.current.click();
	};

	const filesSelected = () => {
		if (fileInputRef.current.files.length) {
			handleFiles(fileInputRef.current.files);
		}
	};

	const removeFile = (name) => {
		const validFileIndex = validFiles.findIndex((e) => e.name === name);
		validFiles.splice(validFileIndex, 1);
		setValidFiles([...validFiles]);
		const selectedFileIndex = selectedFiles.findIndex((e) => e.name === name);
		selectedFiles.splice(selectedFileIndex, 1);
		setSelectedFiles([...selectedFiles]);
		const unsupportedFileIndex = unsupportedFiles.findIndex((e) => e.name === name);
		if (unsupportedFileIndex !== -1) {
			unsupportedFiles.splice(unsupportedFileIndex, 1);
			setUnsupportedFiles([...unsupportedFiles]);
		}
	};

	const uploadFiles = () => {
		cb(selectedFiles);
	};

	const validateFile = (file) => {
		if (validTypes.indexOf(file.type) === -1) {
			return false;
		}
		return true;
	};

	const fileSize = (size) => {
		if (size === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
		const i = Math.floor(Math.log(size) / Math.log(k));
		return parseFloat((size / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const fileType = (fileName) => {
		return fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length) || fileName;
	};

	return (
		<StyledContainer>
			{unsupportedFiles.length ? <StyledErrorMessage>Please remove all unsupported files.</StyledErrorMessage> : ""}
			{(single && validFiles.length < 1) || !single ? (
				<StyledDropContainer
					onDragOver={dragOver}
					onDragEnter={dragEnter}
					onDragLeave={dragLeave}
					onDrop={fileDrop}
					onClick={fileInputClicked}
				>
					<StyledDropMessage>
						<StyledUploadIcon>
							<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
								<path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
								<path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
							</svg>
						</StyledUploadIcon>
						Drag and drop files here or click to upload
					</StyledDropMessage>
					<input ref={fileInputRef} type="file" multiple onChange={filesSelected} />
				</StyledDropContainer>
			) : (
				""
			)}
			<StyledFileDisplayContainer>
				{validFiles.map((data, i) => (
					<StyledFileStatusBar key={i}>
						<div>
							<StyledFileTypeLogo>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
									<path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
								</svg>
							</StyledFileTypeLogo>
							<StyledFileType>{fileType(data.name)}</StyledFileType>
							<StyledFileName>{data.name}</StyledFileName>
							<StyledFileSize>({fileSize(data.size)})</StyledFileSize>{" "}
							{data.invalid && <StyledFileErrorMessage>({errorMessage})</StyledFileErrorMessage>}
						</div>
						<StyledFileRemove onClick={() => removeFile(data.name)}>
							<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
								<path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
								<path
									fillRule="evenodd"
									d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
								/>
							</svg>
						</StyledFileRemove>
					</StyledFileStatusBar>
				))}
			</StyledFileDisplayContainer>
			{unsupportedFiles.length === 0 && validFiles.length ? (
				<StyledActionButtons right>
					<Button icon={SaveIcon} onClick={() => uploadFiles()}>
						Upload files
					</Button>
				</StyledActionButtons>
			) : (
				""
			)}
		</StyledContainer>
	);
};
export default Dropzone;

Dropzone.propTypes = {
	single: PropTypes.bool,
	cb: PropTypes.func,
	validTypes: PropTypes.array.isRequired,
};

Dropzone.defaultProps = {
	single: false,
};
